import { Star, ExternalLink } from "lucide-react";
import { GOOGLE_REVIEWS, GOOGLE_REVIEWS_URL, GOOGLE_REVIEWS_TOTAL, GOOGLE_REVIEWS_RATING } from "@/lib/reviews";
import Reveal from "@/components/site/Reveal";
import { useT } from "@/lib/i18n";

const Stars = ({ count = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${count} de 5 estrellas`}>
    {Array.from({ length: count }, (_, i) => `star-${i}`).map((id) => (
      <Star key={id} size={15} className="fill-terracotta text-terracotta" />
    ))}
  </div>
);

const GoogleG = ({ size = 16 }) => (
  // Inline Google "G" SVG (no external dep, official 4-color)
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.1z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.5 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C41.4 35.4 44 30 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

const initials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const GoogleReviews = () => {
  const t = useT();
  return (
  <section
    className="py-24 md:py-32 bg-ivory"
    data-testid="google-reviews-section"
    aria-labelledby="google-reviews-heading"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">
            {t("reviews.eyebrow.google")}
          </p>
          <h2
            id="google-reviews-heading"
            className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05]"
          >
            {t("reviews.title1")} <span className="italic text-terracotta">{t("reviews.title2")}</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 lg:justify-end">
            <div className="flex items-center gap-3">
              <GoogleG size={26} />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-serif text-2xl text-charcoal leading-none">{GOOGLE_REVIEWS_RATING}</span>
                  <Stars />
                </div>
                <p className="text-[11px] tracking-[0.18em] uppercase text-charcoal/55 mt-1">
                  {t("reviews.basedOn")} {GOOGLE_REVIEWS_TOTAL} {t("reviews.reviewsWord")}
                </p>
              </div>
            </div>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-cta"
              className="inline-flex items-center gap-2 text-sm text-charcoal border-b border-terracotta pb-1 hover:text-terracotta transition-colors w-fit"
            >
              {t("reviews.viewAll")} <ExternalLink size={14} />
            </a>
          </div>
        </Reveal>
      </div>

      {/* Review cards */}
      <div className="grid md:grid-cols-3 gap-6 lg:gap-7">
        {GOOGLE_REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.08}>
            <article
              data-testid={`google-review-${i}`}
              className="relative bg-white rounded-3xl border border-sand/70 p-7 md:p-8 h-full flex flex-col hover:shadow-xl hover:shadow-charcoal/5 transition-shadow"
            >
              <div className="flex items-center justify-between mb-5">
                <Stars />
                <GoogleG size={18} />
              </div>
              <p className="text-charcoal/85 text-[15px] leading-relaxed flex-1">
                "{r.text}"
              </p>
              <div className="mt-7 pt-5 border-t border-sand flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sand text-charcoal/70 flex items-center justify-center text-[13px] font-medium tracking-wider">
                  {initials(r.name)}
                </div>
                <div className="leading-tight">
                  <p className="text-[14px] text-charcoal font-medium">{r.name}</p>
                  {r.role && (
                    <p className="text-[11.5px] text-charcoal/55 mt-0.5">{r.role}</p>
                  )}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export default GoogleReviews;
